import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { KeyRoundIcon, LockIcon, ShieldAlertIcon, XIcon } from 'lucide-react';
interface CompanyAccessModalProps {
  isOpen: boolean;
  hasPasscode: boolean;
  onClose: () => void;
  onCreatePasscode: (passcode: string) => void;
  onVerifyPasscode: (passcode: string) => boolean;
  onResetPasscode: () => void;
}
export function CompanyAccessModal({
  isOpen,
  hasPasscode,
  onClose,
  onCreatePasscode,
  onVerifyPasscode,
  onResetPasscode
}: CompanyAccessModalProps) {
  const [passcode, setPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setPasscode('');
    setConfirmPasscode('');
    setError(null);
  }, [isOpen, hasPasscode]);
  const handleSubmit = () => {
    const value = passcode.trim();
    if (!value) {
      setError('Enter a passcode to continue.');
      return;
    }
    if (hasPasscode) {
      if (!onVerifyPasscode(value)) {
        setError('That passcode is incorrect.');
        return;
      }
      return;
    }
    if (value.length < 4) {
      setError('Use at least 4 characters.');
      return;
    }
    if (value !== confirmPasscode.trim()) {
      setError('Passcodes do not match.');
      return;
    }
    onCreatePasscode(value);
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-24" data-mp="39vhr401ecsm8">
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-id="element-25" data-mp="39tx0umm47vu6">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-26" data-mp="3esqdkctkvita" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} role="dialog" aria-modal="true" aria-labelledby="company-access-title" className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900 shadow-2xl" data-id="element-27" data-mp="3ntoiqyfr5wdg">
          <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-4" data-id="element-28" data-mp="35tlsbnwwls0n">
            <div className="flex items-center gap-3" data-id="element-29" data-mp="31ovxm2dzejhb">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-500/25 bg-emerald-500/10" data-id="element-30" data-mp="3fposio873gky">
                <LockIcon className="h-5 w-5 text-emerald-400" data-id="element-31" data-mp="3aqc80a4sqllj" />
              </div>
              <div data-id="element-32" data-mp="3pezyi1zsc54m">
                <h2 id="company-access-title" className="text-lg font-bold text-white" data-id="element-33" data-mp="3pfi4hqc91xpq">
                  {hasPasscode ? 'Unlock company view' : 'Set a company passcode'}
                </h2>
                <p className="text-xs text-slate-500" data-id="element-34" data-mp="3thmso9jxi24z">
                  Olympian Mortgage license details
                </p>
              </div>
            </div>
            <button onClick={onClose} className="-mr-2 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400" aria-label="Close" data-id="element-35" data-mp="gytvj9knprz0">
              <XIcon className="h-5 w-5" data-id="element-36" data-mp="bxcvthdwsbk0" />
            </button>
          </div>

          <div className="space-y-4 p-6" data-id="element-37" data-mp="3ubvxbxvfnnlj">
            <p className="text-sm leading-5 text-slate-400" data-id="element-38" data-mp="3uauakw521cnz">
              {hasPasscode ? 'Enter your passcode to reveal the company compliance row, coverage map, and bond details.' : 'Create a passcode to keep the company-level license details hidden from anyone else using this tracker.'}
            </p>

            <div data-id="element-39" data-mp="3bdje1jat4xc8">
              <label htmlFor="company-passcode" className="mb-2 block text-xs font-medium uppercase tracking-wider text-slate-400" data-id="element-40" data-mp="3bf48za11aiam">
                Passcode
              </label>
              <div className="flex items-center rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 transition-colors focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30" data-id="element-41" data-mp="38bok2wqscwah">
                <KeyRoundIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-42" data-mp="2ykafwbnw38b0" />
                <input id="company-passcode" type="password" value={passcode} autoFocus onChange={event => {
                setPasscode(event.target.value);
                setError(null);
              }} onKeyDown={event => {
                if (event.key === 'Enter') handleSubmit();
              }} placeholder="••••••" className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600" data-id="element-43" data-mp="2uiyzmwqwnzfh" />
              </div>
            </div>

            {!hasPasscode && <div data-id="element-44" data-mp="1rrxt754rya96">
                <label htmlFor="company-passcode-confirm" className="mb-2 block text-xs font-medium uppercase tracking-wider text-slate-400" data-id="element-45" data-mp="1rqcz1gmdgtbu">
                  Confirm passcode
                </label>
                <div className="flex items-center rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 transition-colors focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30" data-id="element-46" data-mp="25k3y1v2h8utb">
                  <KeyRoundIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-47" data-mp="1dt7w3phygakt" />
                  <input id="company-passcode-confirm" type="password" value={confirmPasscode} onChange={event => {
                setConfirmPasscode(event.target.value);
                setError(null);
              }} onKeyDown={event => {
                if (event.key === 'Enter') handleSubmit();
              }} placeholder="••••••" className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600" data-id="element-48" data-mp="1ht8cvgsj0xv1" />
                </div>
              </div>}

            {error && <p role="alert" className="text-xs font-medium text-red-400" data-id="element-49" data-mp="136o61xlf3ucv">
                {error}
              </p>}

            <div className="flex items-start gap-2 rounded-xl border border-slate-800 bg-slate-800/30 px-3 py-2.5" data-id="element-50" data-mp="fdfuchrn8epx">
              <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-500" data-id="element-51" data-mp="p5ln3tppz066" />
              <p className="text-[11px] leading-4 text-slate-500" data-id="element-52" data-mp="kevfe9ttml8b">
                This passcode is stored on this device only and hides company
                details in the interface. It isn't a substitute for
                server-side access control.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-slate-800 px-6 py-4" data-id="element-53" data-mp="23dtdozz55t0t">
            {hasPasscode ? <button onClick={onResetPasscode} className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-300" data-id="element-54" data-mp="285barcy1n9gi">
                Reset passcode
              </button> : <span data-id="element-55" data-mp="2m419sy4fbj7i" />}
            <div className="flex items-center gap-3" data-id="element-56" data-mp="2m541375nrnny">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-400 transition-colors hover:text-slate-200" data-id="element-57" data-mp="2m2853oddf925">
                Cancel
              </button>
              <button onClick={handleSubmit} className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-950/40 transition-colors hover:bg-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300" data-id="element-58" data-mp="2i1xv80mgjyi0">
                {hasPasscode ? 'Unlock' : 'Save & unlock'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}